import * as Rx from 'rxjs/Rx'
import * as uuid from 'uuid'
import { ActionsObservable, combineEpics } from 'redux-observable'
import { AnyAction } from 'typescript-fsa'
import { Store } from 'redux'
import { push } from 'react-router-redux'

import ConfigFactory from '../../models/ConfigFactory'
import Dependencies from '../Dependencies'
import Epic from '../Epic'
import State from '../../states'
import {
  App,
  EventHub,
  Login,
  MediaInput,
  Photo,
  Toolbar,
  World
} from '../../actions'
import {
  MoveEvent,
  MoveEventHelper,
  isAbsentEvent,
  isMapObjectEventUpdate
} from '../../io/events'
import { Player } from '../../states/WorldState'
import { Vector, vectorFromPoints, vectorLength } from '../../models/BasicTypes'
import { infoRoute } from '../../states/RouterState'

// Kick off client settings loading
export const loadConfig = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>,
  { logger }: Dependencies
) =>
  action$
    .filter(App.launched.match)
    .do(() => {
      logger.info('Start loading client config')
    })
    .mapTo(World.loadConfig.started(undefined))

// Create client session
export const createClientSession = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>,
  { logger, apiClient, appContext }: Dependencies
) =>
  action$.filter(World.loadConfig.done.match).map(() => {
    logger.info('Create client session')
    const config = store.getState().world.config!
    const factory = new ConfigFactory(apiClient, config)
    appContext.clientSession = factory.createClientSession()
    appContext.clientSession.eventHub.connect()
    return World.sessionCreated()
  })

// Join the world
const join = (action$: ActionsObservable<AnyAction>, _store: Store<State>) =>
  action$
    .filter(Login.updateCredentials.match)
    .filter(action => !!action.payload)
    // XXX:
    .map(() => {
      const distance = Math.random() * 3000
      const radian = Math.random() * Math.PI * 2
      const location = {
        x: 6411795.907530935 + Math.cos(radian) * distance,
        y: 15423617.277098654 + Math.sin(radian) * distance
      }
      return World.join.started(location)
    })

// Join the world after sign up
const joinAfterSignUp = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>,
  { logger, appContext }: Dependencies
) =>
  action$.filter(Login.signUp.done.match).map(() => {
    const state = store.getState()
    // TODO: what if the user not joined?
    const player = state.world.mapObjects[state.world.player!.id]
      .mapObject as Player
    let position = player.position!
    const { lastMoveEvent } = player
    if (lastMoveEvent) {
      const helper = new MoveEventHelper({
        id: '',
        mapObjectID: player.id,
        type: 'move',
        local: true,
        bySelf: false,
        ...lastMoveEvent
      })
      const now = new Date().getTime()
      const elasped = now - lastMoveEvent.createdAt
      position = helper.currentPosition(elasped)
    }
    logger.info('Rejoin after sign up at', position)
    const legacyPosition = appContext.clientSession!.legacyCRS.wanderToLegacy(
      position
    )
    return World.join.started(legacyPosition)
  })

// Subscribe map object channel
const subscribeMapObjectChannel = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>,
  { logger }: Dependencies
) =>
  action$.filter(World.addMapObject.match).concatMap(action => {
    const channel = `/map_objects/${action.payload.id}`
    const state = store.getState()
    if (channel in state.eventHub.subscriptions) {
      logger.warn(`Channel ${channel} already subscribed`)
      return Rx.Observable.empty()
    }
    return Rx.Observable.of(
      EventHub.subscribe({
        channel,
        tag: 'map_object'
      })
    )
  })

// Remove map object when it's absent
const removeMapObjectWhenAbsent = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) =>
  action$
    .filter(EventHub.eventUpdate.match)
    .map(action => action.payload)
    .filter(isMapObjectEventUpdate)
    .map(update => update.event)
    .filter(isAbsentEvent)
    .filter(event => !event.migration)
    .map(event => World.removeMapObject(event.mapObjectID))

// Unsubscribe map object channel
const unsubscribeMapObjectChannel = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>,
  { logger }: Dependencies
) =>
  action$.filter(World.removeMapObject.match).concatMap(action => {
    const channel = `/map_objects/${action.payload}`
    const state = store.getState()
    if (!(channel in state.eventHub.subscriptions)) {
      logger.warn(`Channel ${channel} not subscribed`)
      return Rx.Observable.empty()
    }
    return Rx.Observable.of(EventHub.unsubscribe(channel))
  })

// Load map object data
const loadMapObjectData = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) =>
  action$
    .filter(World.addMapObject.match)
    .map(action => World.loadMapObject.started(action.payload.id))

// TODO: move this to higher priority?
// Move current player
const move = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>,
  { logger, apiClient, appContext }: Dependencies
) =>
  action$.filter(World.move.match).switchMap(action => {
    const { dest, focusMapObjectPosition } = action.payload
    if (!focusMapObjectPosition) {
      return Rx.Observable.empty()
    }
    const { legacyCRS } = appContext.clientSession!
    const player = store.getState().world.player!
    // TODO: maybe consider using performance.now() instead?
    // ref: https://developer.mozilla.org/en-US/docs/Web/API/Performance/now
    const now = new Date().getTime() / 1000
    const localID = 'EV' + uuid()
    const vector: Vector = vectorFromPoints(focusMapObjectPosition, dest)
    const distance = vectorLength(vector)
    // TODO: this calculation is not right
    const duration =
      distance / (player.speed * legacyCRS.legacyToWanderScale) * 1000
    const endsAt = now + duration
    const localEvent: MoveEvent = {
      id: localID,
      type: 'move',
      mapObjectID: player.id,
      source: focusMapObjectPosition,
      destination: dest,
      createdAt: now,
      local: true,
      bySelf: false,
      endsAt
    }
    const legacySrc = legacyCRS.wanderToLegacy(localEvent.source)
    const legacyDest = legacyCRS.wanderToLegacy(localEvent.destination)
    return Rx.Observable.of(World.localEventUpdate(localEvent)).concat(
      apiClient
        .moveMapObject({
          id: player.id,
          src: legacySrc,
          dest: legacyDest,
          localID
        })
        .catch(error => {
          logger.info('Failed to move', error)
          return Rx.Observable.empty()
        })
        .ignoreElements()
    )
  })

// Press map object
const pressMapObject = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) =>
  action$
    .filter(World.mapObjectPressed.match)
    // TODO: get the link from router state?
    .map(({ payload }) => push(`/photo/${payload}`))

// Photo modal close
const photoModalClose = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) => action$.filter(Photo.closeModal.match).map(() => push('/'))

// Tap info modal
const tapInfoModalButton = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) =>
  action$.filter(Toolbar.showInfoModal.match).map(() => push(infoRoute.path!))

// Close info modal
const closeInfoModalButton = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) => action$.filter(Toolbar.closeInfoModal.match).map(() => push('/'))

// Submit photo
const submitPhoto = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>
) =>
  action$.filter(MediaInput.submitPhoto.match).mergeMap(({ payload }) => {
    const state = store.getState()
    const user = state.login.user!
    if (user.guest) {
      return Rx.Observable.of(Toolbar.showSignUpPromoteModal())
    }
    return Rx.Observable.of(MediaInput.uploadPhoto.started(payload))
  })

export default combineEpics<Epic>(
  loadConfig,
  createClientSession,
  join,
  joinAfterSignUp,
  subscribeMapObjectChannel,
  unsubscribeMapObjectChannel,
  removeMapObjectWhenAbsent,
  loadMapObjectData,
  move,
  pressMapObject,
  photoModalClose,
  tapInfoModalButton,
  closeInfoModalButton,
  submitPhoto
)



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/epics/world/world.ts